/* eslint-disable no-unknown/no-unknown */
// @services/clients/DocumentAssistantRetrievalContextPreviewApi.ts
import * as E from "fp-ts/Either";
import * as t from "io-ts";
import { PathReporter } from "io-ts/PathReporter";

import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";

export const DocumentAssistantRetrievalContextPreviewContract = {
    contractId: "document.assistant.retrieval.contextPreview",
    contextPreviewPath: "/v1/document/assistant/retrieval/listings/{listingId}/context-preview",
} as const;

export const contextPreviewPath = (listingId: string): string =>
    DocumentAssistantRetrievalContextPreviewContract.contextPreviewPath.replace(
        "{listingId}",
        encodeURIComponent(listingId),
    );

export type ContextPreviewApiDecodeResult<T> =
    | {
          readonly type: "ok";
          readonly value: T;
      }
    | {
          readonly type: "error";
          readonly message: string;
      };

export type ContextPreviewJsonPayload =
    | string
    | number
    | boolean
    | object
    | readonly ContextPreviewJsonPayload[];

export type ContextPreviewPurpose = "answerQuestion" | "draftContent" | "prepareCommunication";

export type ContextPreviewRequest = {
    readonly workspaceId: string;
    readonly workspaceVersion: number;
    readonly purpose: ContextPreviewPurpose;
    readonly prompt: string;
    readonly sendRequestAttempted: boolean;
};

export type ContextPreviewRequestTrace = {
    readonly traceId: string;
    readonly requestId: string;
};

export type ContextPreviewSourceTrace = {
    readonly sourceId: string;
    readonly revision: number;
    readonly granularity: string;
    readonly audit: string;
};

export type ContextPreviewCitation = {
    readonly sourceId: string;
    readonly sourceLabel: string;
    readonly evidenceLabel: string;
    readonly priorListingId: OptionTs<string>;
    readonly authority: string;
    readonly confidence: OptionTs<number>;
    readonly authorityGaps: readonly string[];
    readonly rationale: string;
};

export type ContextPreviewEvidenceGroup = {
    readonly bundleCount: number;
    readonly claimCount: number;
    readonly listingIds: readonly string[];
    readonly sourceRefs: readonly string[];
    readonly redactionSafeCount: number;
    readonly requiresReviewCount: number;
};

export type ContextPreviewEvidenceSummary = {
    readonly current: ContextPreviewEvidenceGroup;
    readonly priorListings: ContextPreviewEvidenceGroup;
    readonly draftOnly: ContextPreviewEvidenceGroup;
    readonly authorityGaps: readonly string[];
};

export type ContextPreviewSpanAnchor = {
    readonly startMs: number;
    readonly endMs: number;
};

export type ContextPreviewProvenance = {
    readonly sourceRef: string;
    readonly span: ContextPreviewSpanAnchor;
    readonly speaker: string;
    readonly observedAt: string;
};

export type ContextPreviewConfirmedInference = {
    readonly claimId: string;
    readonly listingId: string;
    readonly kind: string;
    readonly value: string;
    readonly disposition: string;
    readonly provenance: ContextPreviewProvenance;
};

export type ContextPreviewUnresolvedQuestion = {
    readonly question: string;
    readonly listingId: string;
    readonly evidenceSource: string;
    readonly provenance: ContextPreviewProvenance;
};

export type ContextPreviewSourceRevision = {
    readonly sourceId: string;
    readonly revision: number;
};

export type ContextPreviewCacheState = {
    readonly freshness: string;
    readonly workspaceVersion: number;
    readonly profileCacheKey: string;
    readonly sourceRevisions: readonly ContextPreviewSourceRevision[];
    readonly deletionNoticeCount: number;
};

export type ContextPreviewReadiness = {
    readonly draftAllowed: boolean;
    readonly sendGateType: OptionTs<string>;
    readonly sendAuthorized: boolean;
    readonly noSendReady: boolean;
};

export type ContextPreviewAuthoritySummary = {
    readonly manualAssertionCount: number;
    readonly unresolvedCitationCount: number;
    readonly priorListingCitationCount: number;
    readonly authorityGaps: readonly string[];
};

export type ContextPreviewResult = {
    readonly workspaceId: string;
    readonly workspaceVersion: number;
    readonly listingId: string;
    readonly purpose: string;
    readonly promptEcho: string;
    readonly sources: readonly ContextPreviewSourceTrace[];
    readonly citations: readonly ContextPreviewCitation[];
    readonly evidenceSummary: ContextPreviewEvidenceSummary;
    readonly confirmedInferences: readonly ContextPreviewConfirmedInference[];
    readonly unresolvedQuestions: readonly ContextPreviewUnresolvedQuestion[];
    readonly cacheState: ContextPreviewCacheState;
    readonly readiness: ContextPreviewReadiness;
    readonly authoritySummary: ContextPreviewAuthoritySummary;
    readonly readOnly: boolean;
    readonly profileMode: string;
    readonly trace: ContextPreviewRequestTrace;
};

export type ContextPreviewRefusalCode =
    | "invalidListingId"
    | "invalidRequest"
    | "readDenied"
    | "sendAttemptRefused"
    | "serviceUnavailable";

export type ContextPreviewErrorCode = ContextPreviewRefusalCode | "networkError" | "responseDecodeError";

export type ContextPreviewRefusal = {
    readonly error: ContextPreviewRefusalCode;
    readonly message: string;
    readonly trace: ContextPreviewRequestTrace;
};

export type ContextPreviewError = {
    readonly error: ContextPreviewErrorCode;
    readonly message: string;
    readonly trace: OptionTs<ContextPreviewRequestTrace>;
};

const jsonNull = (): ContextPreviewJsonPayload => JSON.parse("null") as ContextPreviewJsonPayload;

const optionCodec = <A>(codec: t.Decoder<unknown, A>): t.Type<OptionTs<A>, unknown, unknown> =>
    new t.Type<OptionTs<A>, unknown, unknown>(
        "Option",
        (value): value is OptionTs<A> => typeof value === "object",
        (input, context) => {
            if (input === jsonNull()) {
                return t.success(Option.none<A>());
            }
            const decoded = codec.decode(input);
            if (E.isRight(decoded)) {
                return t.success(Option.some(decoded.right));
            }
            return t.failure(input, context, PathReporter.report(decoded).join("; "));
        },
        (value) => Option.fold(value, jsonNull, (inner) => inner as ContextPreviewJsonPayload),
    );

const purposeCodec = t.keyof({
    answerQuestion: 0,
    draftContent: 0,
    prepareCommunication: 0,
});

const refusalCodeCodec = t.keyof({
    invalidListingId: 0,
    invalidRequest: 0,
    readDenied: 0,
    sendAttemptRefused: 0,
    serviceUnavailable: 0,
});

export const contextPreviewRequestTraceCodec = t.type({
    traceId: t.string,
    requestId: t.string,
});

export const contextPreviewRequestCodec = t.type({
    workspaceId: t.string,
    workspaceVersion: t.number,
    purpose: purposeCodec,
    prompt: t.string,
    sendRequestAttempted: t.boolean,
});

export const contextPreviewSourceTraceCodec = t.type({
    sourceId: t.string,
    revision: t.number,
    granularity: t.string,
    audit: t.string,
});

export const contextPreviewCitationCodec = t.type({
    sourceId: t.string,
    sourceLabel: t.string,
    evidenceLabel: t.string,
    priorListingId: optionCodec(t.string),
    authority: t.string,
    confidence: optionCodec(t.number),
    authorityGaps: t.array(t.string),
    rationale: t.string,
});

export const contextPreviewEvidenceGroupCodec = t.type({
    bundleCount: t.number,
    claimCount: t.number,
    listingIds: t.array(t.string),
    sourceRefs: t.array(t.string),
    redactionSafeCount: t.number,
    requiresReviewCount: t.number,
});

export const contextPreviewEvidenceSummaryCodec = t.type({
    current: contextPreviewEvidenceGroupCodec,
    priorListings: contextPreviewEvidenceGroupCodec,
    draftOnly: contextPreviewEvidenceGroupCodec,
    authorityGaps: t.array(t.string),
});

export const contextPreviewSpanAnchorCodec = t.type({
    startMs: t.number,
    endMs: t.number,
});

export const contextPreviewProvenanceCodec = t.type({
    sourceRef: t.string,
    span: contextPreviewSpanAnchorCodec,
    speaker: t.string,
    observedAt: t.string,
});

export const contextPreviewConfirmedInferenceCodec = t.type({
    claimId: t.string,
    listingId: t.string,
    kind: t.string,
    value: t.string,
    disposition: t.string,
    provenance: contextPreviewProvenanceCodec,
});

export const contextPreviewUnresolvedQuestionCodec = t.type({
    question: t.string,
    listingId: t.string,
    evidenceSource: t.string,
    provenance: contextPreviewProvenanceCodec,
});

export const contextPreviewSourceRevisionCodec = t.type({
    sourceId: t.string,
    revision: t.number,
});

export const contextPreviewCacheStateCodec = t.type({
    freshness: t.string,
    workspaceVersion: t.number,
    profileCacheKey: t.string,
    sourceRevisions: t.array(contextPreviewSourceRevisionCodec),
    deletionNoticeCount: t.number,
});

export const contextPreviewReadinessCodec = t.type({
    draftAllowed: t.boolean,
    sendGateType: optionCodec(t.string),
    sendAuthorized: t.boolean,
    noSendReady: t.boolean,
});

export const contextPreviewAuthoritySummaryCodec = t.type({
    manualAssertionCount: t.number,
    unresolvedCitationCount: t.number,
    priorListingCitationCount: t.number,
    authorityGaps: t.array(t.string),
});

export const contextPreviewResultCodec = t.type({
    workspaceId: t.string,
    workspaceVersion: t.number,
    listingId: t.string,
    purpose: t.string,
    promptEcho: t.string,
    sources: t.array(contextPreviewSourceTraceCodec),
    citations: t.array(contextPreviewCitationCodec),
    evidenceSummary: contextPreviewEvidenceSummaryCodec,
    confirmedInferences: t.array(contextPreviewConfirmedInferenceCodec),
    unresolvedQuestions: t.array(contextPreviewUnresolvedQuestionCodec),
    cacheState: contextPreviewCacheStateCodec,
    readiness: contextPreviewReadinessCodec,
    authoritySummary: contextPreviewAuthoritySummaryCodec,
    readOnly: t.boolean,
    profileMode: t.string,
    trace: contextPreviewRequestTraceCodec,
});

export const contextPreviewRefusalCodec = t.type({
    error: refusalCodeCodec,
    message: t.string,
    trace: contextPreviewRequestTraceCodec,
});

const decodeWith = <T>(codec: t.Decoder<unknown, T>, payload: unknown, label: string): ContextPreviewApiDecodeResult<T> => {
    const decoded = codec.decode(payload);
    if (E.isRight(decoded)) {
        return {
            type: "ok",
            value: decoded.right,
        };
    }

    return {
        type: "error",
        message: `${label} decode failed: ${PathReporter.report(decoded).join("; ")}`,
    };
};

export const decodeContextPreviewRequest = (
    payload: unknown,
): ContextPreviewApiDecodeResult<ContextPreviewRequest> =>
    decodeWith(contextPreviewRequestCodec, payload, "context preview request");

export const decodeContextPreviewResult = (
    payload: unknown,
): ContextPreviewApiDecodeResult<ContextPreviewResult> =>
    decodeWith(contextPreviewResultCodec, payload, "context preview result");

export const decodeContextPreviewRefusal = (
    payload: unknown,
): ContextPreviewApiDecodeResult<ContextPreviewRefusal> =>
    decodeWith(contextPreviewRefusalCodec, payload, "context preview refusal");

export const contextPreviewErrorFromRefusal = (refusal: ContextPreviewRefusal): ContextPreviewError => ({
    error: refusal.error,
    message: refusal.message,
    trace: Option.some(refusal.trace),
});

export const contextPreviewLocalError = (
    error: "networkError" | "responseDecodeError",
    message: string,
): ContextPreviewError => ({
    error,
    message,
    trace: Option.none<ContextPreviewRequestTrace>(),
});

export const encodeContextPreviewRequest = (
    request: ContextPreviewRequest,
): Record<string, string | number | boolean> => ({
    workspaceId: request.workspaceId,
    workspaceVersion: request.workspaceVersion,
    purpose: request.purpose,
    prompt: request.prompt,
    sendRequestAttempted: request.sendRequestAttempted,
});
